// src/pages/Servicios.jsx
import React, { useState, useRef, useEffect } from "react";
import "../styles/servicios.css";
import { ReactComponent as PetfamLogo } from "../assets/PetfamLogo.svg"; // ✅ SVG
import heroImg from "../assets/hero-servicios.webp";
import etologiaImg from "../assets/etologia-img.webp";
import veterinariaImg from "../assets/veterinaria-img.webp";
import historiaImg from "../assets/historia.webp";
import calendarioImg from "../assets/calendario.webp";
import carnetImg from "../assets/carnet.webp";

import { Link, useLocation } from "react-router-dom";
import { useIntersection } from "../hooks/useIntersection";
import PhoneMockup from "../components/PhoneMockup";

const ChevronDown = ({ className = "" }) => (
  <svg
    className={className}
    width="22"
    height="22"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path d="M6.7 9.3a1 1 0 0 1 1.4 0L12 13.2l3.9-3.9a1 1 0 1 1 1.4 1.4l-4.6 4.6a1 1 0 0 1-1.4 0L6.7 10.7a1 1 0 0 1 0-1.4z" />
  </svg>
);

const Servicios = () => {
  const [menuAbierto, setMenuAbierto] = useState(false);
  const menuRef = useRef(null);

  // Desplegables
  const [openEtologia, setOpenEtologia] = useState(false);
  const [openVeterinaria, setOpenVeterinaria] = useState(false);
  const [openHistoria, setOpenHistoria] = useState(false);
  const [openCalendario, setOpenCalendario] = useState(false);
  const [openCarnet, setOpenCarnet] = useState(false);

  // Scroll dots: [0]=hero, [1]=cards, [2]=phone
  const sectionsRef = useRef([]);
  const [activeSection, setActiveSection] = useState(0);

  const toggleMenu = () => setMenuAbierto((v) => !v);

  // Cerrar menú al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuAbierto(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Activar dot según sección más cercana al centro
  useEffect(() => {
    const computeActive = () => {
      const viewportCenter = window.innerHeight / 2;
      let bestIndex = 0;
      let bestDist = Infinity;

      sectionsRef.current.forEach((el, i) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const sectionCenter = r.top + r.height / 2;
        const dist = Math.abs(sectionCenter - viewportCenter);
        if (dist < bestDist) {
          bestDist = dist;
          bestIndex = i;
        }
      });

      setActiveSection(bestIndex);
    };

    computeActive();
    window.addEventListener("scroll", computeActive, { passive: true });
    window.addEventListener("resize", computeActive);
    return () => {
      window.removeEventListener("scroll", computeActive);
      window.removeEventListener("resize", computeActive);
    };
  }, []);

  const scrollToSection = (index) => {
    sectionsRef.current[index]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // Animaciones al entrar en viewport
  useIntersection(".bounce-in");
  useIntersection(".tilt-pop"); // bloque del celular

  // ── Scroll al header si vengo desde el hero (Home → Servicios)
  const location = useLocation();
  useEffect(() => {
    if (location.state?.scrollTop) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      // Limpia el state para que no vuelva a disparar en navegaciones internas
      window.history.replaceState({}, "");
    }
  }, [location]);

  return (
    <>
      <div className="servicios-container">
        {/* HEADER */}
        <header className="header">
          <div className="header-container">
            <div className="nav-left">
              <PetfamLogo className="logo" aria-label="Logo Petfam" />
            </div>

            <nav className="nav-center">
              <Link to="/">Inicio</Link>
              <Link to="/servicios" className="active">
                Servicios
              </Link>
            </nav>

            <div className="nav-right">
              <div className="user-menu" ref={menuRef}>
                <button
                  className="icon-user"
                  onClick={toggleMenu}
                  aria-label="Perfil"
                >
                  {/* icono usuario */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                    />
                  </svg>
                  {/* caret */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="size-6"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.53 16.28a.75.75 0 0 1-1.06 0l-7.5-7.5a.75.75 0 0 1 1.06-1.06L12 14.69l6.97-6.97a.75.75 0 1 1 1.06 1.06l-7.5 7.5Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {menuAbierto && (
                  <div className="dropdown-menu">
                    <Link to="/crear-cuenta" state={{ from: "servicios" }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        width="20"
                        height="20"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z"
                        />
                      </svg>
                      Suscribirse
                    </Link>
                    <hr />
                    <Link to="/login" state={{ from: "servicios" }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        width="20"
                        height="20"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
                        />
                      </svg>
                      Iniciar sesión
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* HERO SERVICIOS */}
        <section
          ref={(el) => (sectionsRef.current[0] = el)}
          className="hero-servicios"
        >
          <div className="hero-content">
            <div className="hero-texto">
              <h1>
                Conoce nuestros
                <br />
                servicios
              </h1>
            </div>
            <img src={heroImg} alt="Perro y gato" className="hero-img" loading="lazy" />
          </div>
        </section>

        {/* Curva separadora */}
        <svg
          className="curva-heros"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
        >
          <path
            fill="#f2d3b1"
            d="M0,192 C360,320 1080,80 1440,192 L1440,320 L0,320 Z"
          />
        </svg>

        {/* TARJETAS DE SERVICIOS */}
        <section
          ref={(el) => (sectionsRef.current[1] = el)}
          className="cards-wrapper reveal-stagger"
        >
          <div className="cards-grid">
            {/* Etología */}
            <article className={`servicio-card etologia ${openEtologia ? "is-open" : ""}`}>
              <div className="card-inner">
                <div className="card-text">
                  <div className="card-title">
  <span className="title-main">Etología</span>
  <span className="title-sub">(veterinaria conductual)</span>
</div>
                  <button
                    className="chevron-btn"
                    aria-label="Abrir descripción etología"
                    aria-expanded={openEtologia}
                    onClick={() => setOpenEtologia((v) => !v)}
                  >
                    <ChevronDown
                      className={`chevron ${openEtologia ? "open" : ""}`}
                    />
                  </button>
                </div>
                <div className="card-media right">
                  <img src={etologiaImg} alt="Consulta de etología" loading="lazy" />
                </div>
              </div>

              {openEtologia && (
                <div
                  className="card-overlay"
                  onClick={() => setOpenEtologia(false)}
                >
                  <div
                    className="info-box"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="x-btn"
                      onClick={() => setOpenEtologia(false)}
                    >
                      ✖
                    </button>
                    <p>
                      Nuestros etólogos te ayudarán a entender, corregir y
                      mejorar la conducta de tu mascota con orientación
                      profesional.
                    </p>
                  </div>
                </div>
              )}
            </article>

            {/* Veterinaria */}
            <article className={`servicio-card veterinaria ${openVeterinaria ? "is-open" : ""}`}>
              <div className="card-inner">
                <div className="card-media left">
                  <img src={veterinariaImg} alt="Consulta veterinaria" loading="lazy" />
                </div>
                <div className="card-text">
                  <h3 className="card-title dark">Veterinaria</h3>
                  <button
                    className="chevron-btn"
                    aria-expanded={openVeterinaria}
                    onClick={() => setOpenVeterinaria((v) => !v)}
                  >
                    <ChevronDown
                      className={`chevron dark ${
                        openVeterinaria ? "open" : ""
                      }`}
                    />
                  </button>
                </div>
              </div>

              {openVeterinaria && (
                <div
                  className="card-overlay"
                  onClick={() => setOpenVeterinaria(false)}
                >
                  <div
                    className="info-box"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="x-btn"
                      onClick={() => setOpenVeterinaria(false)}
                    >
                      ✖
                    </button>
                    <p>
                      Acceso ilimitado a veterinarios para resolver dudas,
                      orientar y cuidar la salud de tu mascota.
                    </p>
                  </div>
                </div>
              )}
            </article>

            {/* Historia Clínica */}
            <article className={`servicio-card historia ${openHistoria ? "is-open" : ""}`}>
              <div className="card-inner">
                <div className="card-text">
                  <h3 className="card-title dark">
                    Historia
                    <br />
                    Clínica
                  </h3>
                  <button
                    className="chevron-btn"
                    aria-expanded={openHistoria}
                    onClick={() => setOpenHistoria((v) => !v)}
                  >
                    <ChevronDown
                      className={`chevron dark ${
                        openHistoria ? "open" : ""
                      }`}
                    />
                  </button>
                </div>
                <div className="card-media right">
                  <img src={historiaImg} alt="Historia clínica" loading="lazy" />
                </div>
              </div>

              {openHistoria && (
                <div
                  className="card-overlay"
                  onClick={() => setOpenHistoria(false)}
                >
                  <div
                    className="info-box"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="x-btn"
                      onClick={() => setOpenHistoria(false)}
                    >
                      ✖
                    </button>
                    <p>
                      Consulta y gestiona la historia clínica de tu
                      mascota: vacunas, tratamientos, citas y procedimientos para tener el control en todo momento.
                    </p>
                  </div>
                </div>
              )}
            </article>

            {/* Calendario (mismo comportamiento que Veterinaria) */}
            <article className={`servicio-card calendario ${openCalendario ? "is-open" : ""}`}>
              <div className="card-inner">
                <div className="card-media left">
                  <img src={calendarioImg} alt="Calendario de mascota" loading="lazy" />
                </div>
                <div className="card-text">
                  <div className="card-title">
  <span className="title-main">Calendario</span>
  <span className="title-sub">(recordatorios)</span>
</div>
                  <button
                    className="chevron-btn"
                    aria-label="Abrir descripción calendario"
                    aria-expanded={openCalendario}
                    onClick={() => setOpenCalendario((v) => !v)}
                  >
                    <ChevronDown
                      className={`chevron ${openCalendario ? "open" : ""}`}
                    />
                  </button>
                </div>
              </div>

              {openCalendario && (
                <div
                  className="card-overlay"
                  onClick={() => setOpenCalendario(false)}
                >
                  <div
                    className="info-box"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="x-btn"
                      aria-label="Cerrar descripción calendario"
                      onClick={() => setOpenCalendario(false)}
                    >
                      ✖
                    </button>
                    <p>
                      Programa y visualiza recordatorios de vacunas,
                      desparasitaciones, consultas y controles importantes.
                    </p>
                  </div>
                </div>
              )}
            </article>

            {/* Carnet */}
            <article className={`servicio-card carnet ${openCarnet ? "is-open" : ""}`}>
              <div className="card-inner">
                <div className="card-text">
                  <h3 className="card-title">Carnet</h3>
                  <button
                    className="chevron-btn"
                    aria-expanded={openCarnet}
                    onClick={() => setOpenCarnet((v) => !v)}
                  >
                    <ChevronDown
                      className={`chevron ${openCarnet ? "open" : ""}`}
                    />
                  </button>
                </div>
                <div className="card-media right">
                  <img src={carnetImg} alt="Carnet digital" loading="lazy" />
                </div>
              </div>

              {openCarnet && (
                <div
                  className="card-overlay"
                  onClick={() => setOpenCarnet(false)}
                >
                  <div
                    className="info-box"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="x-btn"
                      onClick={() => setOpenCarnet(false)}
                    >
                      ✖
                    </button>
                    <p>
                      Carnet digital de tu mascota: información segura,
                      organizada y lista para compartir en segundos.
                    </p>
                  </div>
                </div>
              )}
            </article>
          </div>
        </section>

        {/* Sección del celular (tercer dot) */}
<section
  ref={(el) => (sectionsRef.current[2] = el)}
  className="phone-mockup-wrap tilt-pop"
>
  <PhoneMockup src="videos/app-demo.mp4" />
</section>

        {/* Scroll Dots: 3 puntos */}
        <div className="scroll-dots">
          {["Hero", "Servicios", "App"].map((label, i) => (
            <span
              key={i}
              title={label}
              className={`dot ${activeSection === i ? "active" : ""}`}
              onClick={() => scrollToSection(i)}
            ></span>
          ))}
        </div>
      </div>
    </>
  );
};

export default Servicios;
